import React, { useEffect, useState, useRef } from 'react';
import { getPerformanceResponses } from '../../../../services/performanceService';
import { completeSurvey } from '../../../../services/surveyService';
import { extendObservationPeriod } from '../../../../services/notificationsService';
import { fetchQ20Data } from '../../../../services/surveyService';
import { deleteDailyNotifications, deleteCustomNotifications } from '../../../../services/notificationsService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Box, Typography, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, Slider } from '@mui/material';
import SurveyCalendar from './SurveyCalendar';
import { addDays, isAfter, format, isSameDay, isBefore, isEqual } from 'date-fns';
import { useTranslation } from 'react-i18next';

const SurveyProgress = ({ userId, observationPeriods }) => {
    const [daysCompleted, setDaysCompleted] = useState(0);
    const [totalSurveyDays, setTotalSurveyDays] = useState(0);
    const [loading, setLoading] = useState(true);
    const [surveyPeriods, setSurveyPeriods] = useState([]);
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [questionsAnsweredToday, setQuestionsAnsweredToday] = useState(0);
    const [isInObservationPeriod, setIsInObservationPeriod] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [canRestartObservation, setCanRestartObservation] = useState(false);
    const [todayScheduledNotifications, setTodayScheduledNotifications] = useState(0);
    const [extendDialogOpen, setExtendDialogOpen] = useState(false);
    const [missingNotifications, setMissingNotifications] = useState([]);
    const [currentPeriodCompleted, setCurrentPeriodCompleted] = useState(false);
    const [performanceResponses, setPerformanceResponses] = useState([]);
    const [newTimeSlots, setNewTimeSlots] = useState([]);
    const { t } = useTranslation();
    const [customStopDialogOpen, setCustomStopDialogOpen] = useState(false);
    const [hoursToDelete, setHoursToDelete] = useState(1);
    const [maxHoursToDelete, setMaxHoursToDelete] = useState(1);
    const [lastWorkHour, setLastWorkHour] = useState(null);
    const previousPeriodRef = useRef(null);
    const [hasFutureNotifications, setHasFutureNotifications] = useState(false);
    const [futureNotificationCount, setFutureNotificationCount] = useState(0);
    const [currentHourNotifications, setCurrentHourNotifications] = useState(0);
    const [todayFutureNotifications, setTodayFutureNotifications] = useState(0);

    // New state for calendar-based progress
    const [calendarDaysPassed, setCalendarDaysPassed] = useState(0);
    const [calendarDaysTotal, setCalendarDaysTotal] = useState(0);
    const [daysFullyCompleted, setDaysFullyCompleted] = useState(0);
    const [daysPartiallyCompleted, setDaysPartiallyCompleted] = useState(0);

    const calculateProgress = async (forceRefresh = false) => {
    try {
        // Don't make API calls if userId is not provided or invalid
        if (!userId || userId === 'undefined') {
            console.warn('SurveyProgress: userId is not defined, skipping progress calculation');
            setLoading(false);
            return;
        }

        if (forceRefresh) {
            setDaysCompleted(0);
            setTotalSurveyDays(0);
            setSurveyPeriods([]);
            setCurrentPeriod(null);
            setQuestionsAnsweredToday(0);
            setPerformanceResponses([]);
            setMissingNotifications([]);
            setNewTimeSlots([]);
            setIsInObservationPeriod(true);
            setCurrentPeriodCompleted(false);
            setCalendarDaysPassed(0);
            setCalendarDaysTotal(0);
            setDaysFullyCompleted(0);
            setDaysPartiallyCompleted(0);
            previousPeriodRef.current = null;

            await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (!observationPeriods || observationPeriods.length === 0) {
            throw new Error('No observation periods found.');
        }

        let performanceData = [];
        try {
            const response = await getPerformanceResponses(userId);
            performanceData = response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.log('No performance responses found yet - this is normal for new users');
                performanceData = [];
            } else {
                throw error;
            }
        }
        
        setPerformanceResponses(performanceData);
        
        const currentDate = new Date();
        
        // Filter out archived periods and sort
        const sortedDateRanges = [...observationPeriods]
            .filter(range => !range.isArchived)  // Filter out archived periods
            .sort((a, b) => 
                new Date(b.startDate) - new Date(a.startDate)
            );

        const activePeriod = sortedDateRanges.find(range => 
            !isBefore(currentDate, new Date(range.startDate))
        ) || sortedDateRanges[0];
        
        const isNewPeriod = previousPeriodRef.current && 
        activePeriod && 
        new Date(activePeriod.startDate).getTime() !== new Date(previousPeriodRef.current.startDate).getTime();
            
        previousPeriodRef.current = activePeriod;
        
        setCurrentPeriod(activePeriod);
        
        const uniqueDaysSet = new Set();
        activePeriod.notifications.forEach((notif) => {
            const notifDate = format(new Date(notif.notificationTime), 'yyyy-MM-dd');
            uniqueDaysSet.add(notifDate);
        });
        
        const totalDays = uniqueDaysSet.size;
        setTotalSurveyDays(totalDays);

        const periodEndDate = new Date(activePeriod.endDate);
        const periodStartDate = new Date(activePeriod.startDate);

        const withinPeriodDates = currentDate >= periodStartDate && currentDate <= periodEndDate;
        setIsInObservationPeriod(withinPeriodDates);

        const todayNotifications = activePeriod.notifications.filter((notif) =>
            isSameDay(new Date(notif.notificationTime), currentDate)
        );

        // Debug logging
        console.log('=== TODAY\'S TASKS DEBUG ===');
        console.log('Current Date:', currentDate);
        console.log('Total notifications in period:', activePeriod.notifications.length);
        console.log('Today\'s notifications count:', todayNotifications.length);
        console.log('Today\'s notifications:', todayNotifications.map(n => ({
            time: n.notificationTime,
            localTime: new Date(n.notificationTime).toLocaleString()
        })));

        setTodayScheduledNotifications(todayNotifications.length);

        const completedDaysSet = new Set();
        const fullyCompletedDaysSet = new Set();
        const partiallyCompletedDaysSet = new Set();
        const rangeUniqueDates = new Set();

        activePeriod.notifications.forEach((notif) => {
            const notifDate = format(new Date(notif.notificationTime), 'yyyy-MM-dd');
            rangeUniqueDates.add(notifDate);
        });

        for (const dateStr of rangeUniqueDates) {
            const date = new Date(dateStr);

            if (isBefore(date, currentDate) || isSameDay(date, currentDate)) {
                const dayNotifications = activePeriod.notifications.filter(notif =>
                    format(new Date(notif.notificationTime), 'yyyy-MM-dd') === dateStr
                );

                let completedCount = 0;
                let totalCount = dayNotifications.length;

                dayNotifications.forEach(notif => {
                    const notifTime = new Date(notif.notificationTime);

                    const hasResponse = performanceData.some(resp =>
                        format(new Date(resp.timestamp), 'yyyy-MM-dd HH') === format(notifTime, 'yyyy-MM-dd HH')
                    );

                    if (hasResponse || notif.isExtended) {
                        completedCount++;
                    }
                });

                // Check if all notifications completed
                const allCompleted = completedCount === totalCount;

                if (allCompleted) {
                    completedDaysSet.add(dateStr);
                    fullyCompletedDaysSet.add(dateStr);
                } else if (completedCount > 0) {
                    // Some but not all completed
                    partiallyCompletedDaysSet.add(dateStr);
                }
            }
        }

        setDaysCompleted(completedDaysSet.size);
        setDaysFullyCompleted(fullyCompletedDaysSet.size);
        setDaysPartiallyCompleted(partiallyCompletedDaysSet.size);

        // Calculate calendar-based progress (using local timezone)
        const startDate = new Date(activePeriod.startDate);
        const endDate = new Date(activePeriod.endDate);

        // Convert to local date (ignore time component)
        const startDateLocal = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateLocal = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const currentDateLocal = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

        // Total calendar days in observation period
        const totalCalendarDays = Math.round((endDateLocal - startDateLocal) / (1000 * 60 * 60 * 24)) + 1;
        setCalendarDaysTotal(totalCalendarDays);

        // Calendar days passed (from start to today, capped at end date)
        let daysPassed = 0;
        if (currentDateLocal >= startDateLocal) {
            const endOfComparison = currentDateLocal > endDateLocal ? endDateLocal : currentDateLocal;
            daysPassed = Math.round((endOfComparison - startDateLocal) / (1000 * 60 * 60 * 24)) + 1;
        }
        setCalendarDaysPassed(daysPassed);

        setSurveyPeriods(observationPeriods);
        
        const missing = activePeriod.notifications.filter((notif) => {
            const notifTime = new Date(notif.notificationTime);
            const startOfCurrentHour = new Date();
            startOfCurrentHour.setMinutes(0, 0, 0);
            
            const isPastNotification = notifTime < startOfCurrentHour;
            
            const hasResponse = performanceData.some((resp) =>
                format(new Date(resp.timestamp), 'yyyy-MM-dd HH') === format(notifTime, 'yyyy-MM-dd HH')
            );
            
            return isPastNotification && !hasResponse;
        });
        
        setMissingNotifications(missing);
        
        const allNotificationsComplete = 
            missing.length === 0 || 
            missing.every(notif => notif.isExtended || notif.hasPerformanceScore);

        if (isNewPeriod) {
            setCurrentPeriodCompleted(false);
        } else {
            const hasPastNotifications = activePeriod.notifications.some(n => 
                new Date(n.notificationTime) < new Date()
            );
            const isActivePeriod = activePeriod.startDate <= currentDate && activePeriod.endDate >= currentDate;
            const isJustCompleted = activePeriod.endDate && isEqual(new Date(activePeriod.endDate), currentDate);
            
            const hasAnyCompletedNotifications = performanceData.some(resp => {
                return activePeriod.notifications.some(notif => 
                    format(new Date(resp.timestamp), 'yyyy-MM-dd HH') === 
                    format(new Date(notif.notificationTime), 'yyyy-MM-dd HH')
                );
            });
            
            setCurrentPeriodCompleted(
                activePeriod.notifications.length > 0 &&
                hasPastNotifications &&
                hasAnyCompletedNotifications &&
                allNotificationsComplete && 
                isActivePeriod && 
                (withinPeriodDates || isJustCompleted)
            );
        }
        
        setCanRestartObservation(allNotificationsComplete);
        
        setLoading(false);
    } catch (error) {
        console.error('Error calculating survey progress:', error);
        setLoading(false);
    }
};

    const fetchTodayPerformance = async () => {
    try {
        if (!currentPeriod) return;

        // Don't make API calls if userId is not provided or invalid
        if (!userId || userId === 'undefined') {
            console.warn('SurveyProgress: userId is not defined, skipping fetchTodayPerformance');
            return;
        }

        const currentDate = new Date();

        const todayNotifications = currentPeriod.notifications.filter((notif) =>
            isSameDay(new Date(notif.notificationTime), currentDate)
        );

        let performanceData = [];
        try {
            const response = await getPerformanceResponses(userId);
            performanceData = response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.log("No performance responses found yet - this is normal for new users");
                performanceData = [];
            } else {
                throw error;
            }
        }

        const todayResponses = performanceData.filter((entry) => {
            const entryDate = new Date(entry.timestamp);

            return todayNotifications.some((notif) => {
                const notifDate = new Date(notif.notificationTime);
                return format(entryDate, 'yyyy-MM-dd HH') === format(notifDate, 'yyyy-MM-dd HH');
            });
        });
        
        setPerformanceResponses(performanceData);
        setQuestionsAnsweredToday(todayResponses.length);
    } catch (error) {
        console.error("Error fetching today's performance responses:", error);
    }
};

    // Rest of the component remains the same...
    const handleOpenExtendDialog = () => setExtendDialogOpen(true);
    
    const handleExtendObservation = async () => {
        try {
            const newNotificationTimes = newTimeSlots.map((slot) => slot.toISOString());

            const response = await extendObservationPeriod(userId, newNotificationTimes);

            toast.success('Observation period extended successfully!');
            await calculateProgress(); // Refresh the data
            setExtendDialogOpen(false);
        } catch (error) {
            console.error('Error extending observation period:', error);
            toast.error('Failed to extend observation period.');
        }
    };

    const calculateNewTimeSlots = async () => {
        try {
            if (!currentPeriod) return;

            // Don't make API calls if userId is not provided or invalid
            if (!userId || userId === 'undefined') {
                console.warn('SurveyProgress: userId is not defined, skipping calculateNewTimeSlots');
                return;
            }

            const q20Data = await fetchQ20Data(userId);

            const { startTime, duration } = q20Data; // Working hours
            const [startHour, startMinute] = startTime.split(':').map(Number);
            const endHour = startHour + duration;

            const existingNotificationTimes = new Set(
                surveyPeriods.flatMap((range) =>
                    range.notifications.map((notif) => format(new Date(notif.notificationTime), 'yyyy-MM-dd HH:mm'))
                )
            );

            const isValidDate = (date) => !isNaN(new Date(date).getTime());

            const missedSlots = missingNotifications
                .filter((notif) => !notif.isExtended && !notif.hasPerformanceScore && isValidDate(notif.notificationTime))
                .map((notif) => {
                    return {
                        ...notif, // Spread the existing properties
                        isExtended: true, // Update the `isExtended` property
                    };
                })
                .map((updatedNotif) => new Date(updatedNotif.notificationTime));

            const endDate = new Date(currentPeriod.endDate);
            const now = new Date();
            const currentStartDate = now > endDate ? now : endDate;

            let currentDay = currentStartDate; // Start from the later of `endDate` or `now`
            let iterationCount = 0;

            const newSlots = [];
            while (missedSlots.length > 0 && iterationCount < missedSlots.length * 7) {
                while (currentDay.getDay() === 0 || currentDay.getDay() === 6) {
                    currentDay = addDays(currentDay, 1); // Skip weekends
                }

                const validDaySlots = [];
                for (let hour = startHour; hour < endHour; hour++) {
                    const potentialSlot = new Date(currentDay.setHours(hour, 0, 0, 0));
                    if (potentialSlot > now) { // Only include slots after the current time
                        validDaySlots.push(format(potentialSlot, 'yyyy-MM-dd HH:mm'));
                    }
                }

                for (let i = 0; i < missedSlots.length;) {
                    const missedSlot = missedSlots[i];
                    const formattedMissedSlot = format(missedSlot, 'HH:mm');

                    const availableSlot = validDaySlots.find(
                        (slot) =>
                            !existingNotificationTimes.has(slot) &&
                            slot.endsWith(formattedMissedSlot)
                    );

                    if (availableSlot) {
                        newSlots.push(new Date(availableSlot));
                        existingNotificationTimes.add(availableSlot);
                        missedSlots.splice(i, 1); // Remove processed slot
                    } else {
                        i++;
                    }
                }

                currentDay = addDays(currentDay, 1);
                iterationCount++;
            }
            newSlots.sort((a, b) => a - b);
            setNewTimeSlots(newSlots);
        } catch (error) {
            console.error('Error calculating new time slots:', error);
        }
    };

    const handleStopWorking = async () => {
        try {
            // Don't make API calls if userId is not provided or invalid
            if (!userId || userId === 'undefined') {
                console.warn('SurveyProgress: userId is not defined, cannot stop working');
                toast.error('User ID is not available. Cannot stop working.');
                return;
            }

            setLoading(true);
            const response = await deleteDailyNotifications(userId);

            toast.success('Work day stopped successfully! Notifications removed.');
            await calculateProgress();
            fetchTodayPerformance();
        } catch (error) {
            console.error('Error stopping work day:', error);
            toast.error('Failed to stop work day. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomStopWorking = async (hoursToDelete) => {
        try {
            setLoading(true);
            const response = await deleteCustomNotifications(userId, hoursToDelete);
    
            toast.success(`Successfully removed ${response.data.deletedCount} notifications.`);
            await calculateProgress();
            await fetchTodayPerformance();
            
            // Refresh available notifications immediately after deletion
            refreshAvailableNotifications();
            
            setCustomStopDialogOpen(false);
        } catch (error) {
            console.error('Error customizing work day:', error);
            toast.error('Failed to update notifications. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const checkFutureNotifications = () => {
        if (!currentPeriod) return;
        
        const now = new Date();
        const today = now.toDateString();
        const currentHour = now.getHours();
        
        // Find notifications remaining today but in future hours
        const remainingTodayNotifications = currentPeriod.notifications.filter(notif => {
            const notifTime = new Date(notif.notificationTime);
            return notifTime.toDateString() === today && notifTime.getHours() > currentHour;
        });
        
        // Find notifications for future days
        const futureDayNotifications = currentPeriod.notifications.filter(notif => {
            const notifTime = new Date(notif.notificationTime);
            return notifTime.toDateString() !== today && notifTime > now;
        });
        console.log('Remaining today notifications:', remainingTodayNotifications);
        
        const allFutureNotifications = [...remainingTodayNotifications, ...futureDayNotifications];
        setFutureNotificationCount(allFutureNotifications.length);
        setHasFutureNotifications(allFutureNotifications.length > 0);
    };

    const handleRestartSurvey = async () => {
        setLoading(true);
        setConfirmDialogOpen(false);

        // Don't make API calls if userId is not provided or invalid
        if (!userId || userId === 'undefined') {
            console.warn('SurveyProgress: userId is not defined, cannot restart survey');
            toast.error('User ID is not available. Cannot restart survey.');
            setLoading(false);
            return;
        }

        try {
            await completeSurvey(userId);

            if (typeof window !== 'undefined') {
                window.location.reload();
            }
            
            setTimeout(async () => {
                await calculateProgress(true);
                await fetchTodayPerformance();
                setLoading(false);
            }, 1500);
        } catch (error) {
            console.error('Error restarting survey:', error);
            setLoading(false);
            setErrorSnackbarOpen(true);
        }
    };

    const handleOpenConfirmDialog = () => {
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleCloseErrorSnackbar = () => {
        setErrorSnackbarOpen(false);
    };

const openCustomStopDialog = () => {
    // Recalculate available notifications every time dialog opens
    refreshAvailableNotifications();
    setCustomStopDialogOpen(true);
};

const refreshAvailableNotifications = () => {
    if (currentPeriod) {
        const now = new Date();
        const currentHour = now.getHours();
        const today = now.toDateString();
        
        // Start and end of current hour
        const startOfCurrentHour = new Date(now);
        startOfCurrentHour.setMinutes(0, 0, 0);
        
        const endOfCurrentHour = new Date(now);
        endOfCurrentHour.setHours(currentHour + 1, 0, 0, 0);
        
        // Current hour notifications for today (ALL in this hour, not just future ones)
        const currentHourNotifs = currentPeriod.notifications.filter(notif => {
            const notifTime = new Date(notif.notificationTime);
            return notifTime.toDateString() === today && 
                   notifTime.getHours() === currentHour;
        });
        
        // Future notifications for today (after current hour)
        const todayFutureNotifs = currentPeriod.notifications.filter(notif => {
            const notifTime = new Date(notif.notificationTime);
            return notifTime.toDateString() === today && 
                   notifTime.getHours() > currentHour;
        });
        
        console.log("Current hour notifications:", currentHourNotifs);
        console.log("Today's future notifications:", todayFutureNotifs);
        
        // Future notifications for other days
        const futureDaysNotifs = currentPeriod.notifications.filter(notif => {
            const notifTime = new Date(notif.notificationTime);
            return notifTime.toDateString() !== today && notifTime > now;
        });
        
        // Set state for UI visibility and messages
        setCurrentHourNotifications(currentHourNotifs.length);
        setTodayFutureNotifications(todayFutureNotifs.length);
        setFutureNotificationCount(todayFutureNotifs.length + futureDaysNotifs.length);
        setHasFutureNotifications(todayFutureNotifs.length + futureDaysNotifs.length > 0);
        
        // Calculate total available hours to delete
        const allTodayNotifs = [...currentHourNotifs, ...todayFutureNotifs];
        if (allTodayNotifs.length > 0) {
            const uniqueHours = new Set(
                allTodayNotifs.map(notif => new Date(notif.notificationTime).getHours())
            );
            
            setMaxHoursToDelete(uniqueHours.size);
            
            const hours = Array.from(uniqueHours);
            if (hours.length > 0) {
                const lastHour = Math.max(...hours);
                setLastWorkHour(lastHour);
            } else {
                setLastWorkHour(null);
            }
        } else {
            setMaxHoursToDelete(0);
            setLastWorkHour(null);
        }
        
        setHoursToDelete(Math.min(1, maxHoursToDelete || 0));
    }
};
    useEffect(() => {
        if (currentPeriod) {
            checkFutureNotifications();
            refreshAvailableNotifications(); // Add this line
        }
    }, [currentPeriod?.notifications]);

 

    useEffect(() => {
        if (observationPeriods && observationPeriods.length > 0) {
            calculateProgress();
            refreshAvailableNotifications(); // Add this line
        }
    }, [userId, observationPeriods]);

    useEffect(() => {
        if (currentPeriod) {
            fetchTodayPerformance();
        }
    }, [currentPeriod]);
    
    useEffect(() => {
        calculateNewTimeSlots();
    }, [missingNotifications, currentPeriod]);

    if (loading) {
        return <Typography>{t('surveyProgress.loading')}</Typography>;
    }

    const percentage = (daysCompleted / totalSurveyDays) * 100;
    const todayPercentage = (questionsAnsweredToday / todayScheduledNotifications) * 100;

    return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 4 }}>
        {showCalendar ? (
            <SurveyCalendar surveyPeriods={surveyPeriods} onReturn={() => setShowCalendar(false)} />
        ) : (
            <>
                {/* Observation Period Display */}
                {currentPeriod && (
                    <Box 
                        sx={{ 
                            mb: 2, 
                            p: 2, 
                            borderRadius: '8px', 
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            textAlign: 'center',
                            width: '100%',
                            maxWidth: '500px'
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {t('surveyProgress.observationPeriod', 'Observation Period')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {format(new Date(currentPeriod.startDate), 'MMM dd, yyyy')} - {format(new Date(currentPeriod.endDate), 'MMM dd, yyyy')}
                        </Typography>
                        {/* Show status based on current date */}
                        {(() => {
                            const now = new Date();
                            const startDate = new Date(currentPeriod.startDate);
                            const endDate = new Date(currentPeriod.endDate);
                            
                            if (now < startDate) {
                                // Period hasn't started yet
                                const daysUntilStart = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
                                return (
                                    <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                                        {t('surveyProgress.startsIn', 'Starts in')} {daysUntilStart} {daysUntilStart === 1 ? t('surveyProgress.day', 'day') : t('surveyProgress.days', 'days')}
                                    </Typography>
                                );
                            } else if (now <= endDate) {
                                // Currently in observation period
                                const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                                return (
                                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                                        {t('surveyProgress.activeStatus', 'Active')} • {daysRemaining} {daysRemaining === 1 ? t('surveyProgress.dayRemaining', 'day remaining') : t('surveyProgress.daysRemaining', 'days remaining')}
                                    </Typography>
                                );
                            } else {
                                // Period has ended
                                return (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {t('surveyProgress.periodEnded', 'Period ended')}
                                    </Typography>
                                );
                            }
                        })()}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {/* Calendar Days Progress */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ width: 100, height: 100 }}>
                            <CircularProgressbar
                                value={(calendarDaysPassed / calendarDaysTotal) * 100}
                                text={`${calendarDaysPassed}/${calendarDaysTotal}`}
                                styles={buildStyles({
                                    textSize: '16px',
                                    textColor: '#3e98c7',
                                    pathColor: calendarDaysPassed === calendarDaysTotal ? '#4caf50' : '#3e98c7',
                                    trailColor: '#d6d6d6',
                                })}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Calendar Days
                        </Typography>
                    </Box>

                    {/* Task Completion Progress */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ width: 100, height: 100 }}>
                            <CircularProgressbar
                                value={percentage}
                                text={`${daysFullyCompleted}/${totalSurveyDays}`}
                                styles={buildStyles({
                                    textSize: '16px',
                                    textColor: '#4caf50',
                                    pathColor: currentPeriodCompleted ? '#4caf50' : '#ff9800',
                                    trailColor: '#d6d6d6',
                                })}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Fully Completed
                        </Typography>
                    </Box>

                    {/* Today's Progress */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ width: 100, height: 100 }}>
                            <CircularProgressbar
                                value={todayPercentage}
                                text={`${questionsAnsweredToday}/${todayScheduledNotifications}`}
                                styles={buildStyles({
                                    textSize: '16px',
                                    textColor: '#3e98c7',
                                    pathColor: questionsAnsweredToday >= todayScheduledNotifications ? '#4caf50' : '#3e98c7',
                                    trailColor: '#d6d6d6',
                                })}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Today's Tasks
                        </Typography>
                    </Box>
                </Box>
          
                {!(missingNotifications.length > 0 && missingNotifications.every(notif => notif.isExtended)) && 
                currentHourNotifications > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={openCustomStopDialog}
                            disabled={loading}
                        >
                            {t('surveyProgress.customizeHours')}
                        </Button>
                    </Box>
                )}

                {currentHourNotifications === 0 && todayFutureNotifications > 0 && (
                    <Box 
                        sx={{ 
                            mt: 3, 
                            p: 3, 
                            width: '100%',
                            maxWidth: '500px',
                            borderRadius: '8px', 
                            bgcolor: 'info.light', 
                            color: 'info.contrastText',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                            No tasks right now
                        </Typography>
                        <Typography variant="body1">
                            You have {todayFutureNotifications} notification{todayFutureNotifications > 1 ? 's' : ''} scheduled for later today.
                        </Typography>
                    </Box>
                )}

                {currentHourNotifications === 0 && todayFutureNotifications === 0 && (
                    <Box 
                        sx={{ 
                            mt: 3, 
                            p: 3,
                            width: '100%',
                            maxWidth: '500px', 
                            borderRadius: '8px', 
                            bgcolor: 'success.light', 
                            color: 'success.contrastText',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body1">
                            {t('surveyProgress.noNotificationsLeft')}
                        </Typography>
                    </Box>
                )}

                {/* Progress Summary */}
                <Box sx={{
                    mt: 3,
                    p: 2,
                    width: '100%',
                    maxWidth: '500px',
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                    boxShadow: 1
                }}>
                    <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                        Progress Summary
                    </Typography>

                    {/* Calendar Progress */}
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            📅 Calendar Progress: <strong>{calendarDaysPassed} of {calendarDaysTotal} days</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {calendarDaysTotal - calendarDaysPassed} calendar days remaining
                        </Typography>
                    </Box>

                    {/* Task Completion */}
                    <Box sx={{ mb: 1, mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            ✅ Fully Completed: <strong>{daysFullyCompleted} days</strong> (all tasks answered)
                        </Typography>
                        {daysPartiallyCompleted > 0 && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                                ⚠️ Partially Completed: <strong>{daysPartiallyCompleted} days</strong> (some tasks missing)
                            </Typography>
                        )}
                    </Box>

                </Box>

                {!isInObservationPeriod && !currentPeriodCompleted && 
                !(missingNotifications.length > 0 && missingNotifications.every(notif => notif.isExtended)) && (
                    <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
                        {t('surveyProgress.incompleteNotice')}
                    </Typography>
                )}

                {!isInObservationPeriod && 
                !(missingNotifications.length > 0 && missingNotifications.every(notif => notif.isExtended)) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleOpenExtendDialog}
                        disabled={loading}
                        sx={{ mt: 2 }}
                        data-tour="extend-observation-button"
                    >
                        {t('surveyProgress.extendObservation')}
                    </Button>
                )}
                
                {missingNotifications.length > 0 && 
                missingNotifications.every(notif => notif.isExtended) && (
                    <>
                        <Typography 
                            variant="body1" 
                            color="success.main" 
                            sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}
                        >
                            {t('surveyProgress.congratulations')}
                        </Typography>
                        
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenConfirmDialog}
                            disabled={loading}
                            sx={{ mt: 2 }}
                        >
                            {t('surveyProgress.restartSurvey')}
                        </Button>
                    </>
                )}
                    
                {!isInObservationPeriod && currentPeriodCompleted && !loading && 
                 !(missingNotifications.length > 0 && missingNotifications.every(notif => notif.isExtended)) && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenConfirmDialog}
                        disabled={loading}
                        sx={{ mt: 2 }}
                    >
                        {t('surveyProgress.restartSurvey')}
                    </Button>
                )}

                {/* All dialogs remain the same */}
                <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
                    <DialogTitle>{t('surveyProgress.confirmRestartTitle')}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('surveyProgress.confirmRestartText')}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseConfirmDialog}>{t('surveyProgress.cancel')}</Button>
                        <Button onClick={handleRestartSurvey} color="primary">
                            {t('surveyProgress.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Custom Stop Dialog */}
                <Dialog
                    open={customStopDialogOpen}
                    onClose={() => setCustomStopDialogOpen(false)}
                    sx={{
                        '& .MuiDialog-paper': {
                            padding: 2,
                            borderRadius: '10px',
                            maxWidth: '400px',
                            width: '100%',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderBottom: '1px solid #ccc',
                            marginBottom: 2,
                        }}
                    >
                        {t('surveyProgress.customizeHoursTitle')}
                    </DialogTitle>
                    
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('surveyProgress.customizeHoursDescription')}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Hours to delete (maximum: {maxHoursToDelete})
                            </Typography>
                            
                            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1">1</Typography>
                                <Slider
                                    value={hoursToDelete}
                                    onChange={(e, newValue) => setHoursToDelete(newValue)}
                                    step={1}
                                    marks
                                    min={1}
                                    max={maxHoursToDelete}
                                    disabled={maxHoursToDelete < 1}
                                    sx={{ flex: 1 }}
                                />
                                <Typography variant="body1">{maxHoursToDelete}</Typography>
                            </Box>
                            
                            <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
                                {hoursToDelete} {hoursToDelete === 1 ? 
                                    t('surveyProgress.hourSelected') : 
                                    t('surveyProgress.hoursSelected')}
                            </Typography>

                            <Box sx={{ 
                                mt: 3, 
                                p: 2, 
                                width: '100%', 
                                borderRadius: '8px', 
                                bgcolor: 'info.light', 
                                color: 'info.contrastText' 
                            }}>
                                <Typography variant="body2">
                                    Your current last hour to work today: <strong>
                                    {lastWorkHour ? `${lastWorkHour}:00` : 'None'}
                                    </strong>
                                </Typography>
                            </Box>
                            
                            {maxHoursToDelete === 0 && (
                                <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                                    {t('surveyProgress.noRemainingNotifications')}
                                </Alert>
                            )}
                        </Box>
                    </DialogContent>
                
                    <DialogActions
                        sx={{
                            justifyContent: 'center',
                            padding: '16px 24px',
                            borderTop: '1px solid #ccc',
                        }}
                    >
                        <Button
                            onClick={() => setCustomStopDialogOpen(false)}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                textTransform: 'none',
                                marginRight: '12px',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        >
                            {t('surveyProgress.cancel')}
                        </Button>
                        
                        <Button
                            onClick={() => {
                                handleCustomStopWorking(hoursToDelete);
                                setCustomStopDialogOpen(false);
                            }}
                            color="primary"
                            variant="contained"
                            disabled={maxHoursToDelete < 1}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                                padding: '8px 16px',
                            }}
                        >
                            {t('surveyProgress.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Extend Dialog */}
                <Dialog
                    open={extendDialogOpen}
                    onClose={() => setExtendDialogOpen(false)}
                    sx={{
                        '& .MuiDialog-paper': {
                            padding: 2,
                            borderRadius: '10px',
                            maxWidth: '600px',
                            width: '100%',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderBottom: '1px solid #ccc',
                            marginBottom: 2,
                        }}
                    >
                        {t('surveyProgress.extendObservationTitle')}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            '& ul': {
                                listStyleType: 'none',
                                padding: 0,
                            },
                            '& li': {
                                padding: '8px 12px',
                                borderRadius: '5px',
                                marginBottom: '8px',
                                backgroundColor: '#f9f9f9',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            },
                        }}
                    >
                        {missingNotifications.length > 0 ? (
                            <>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        marginBottom: '12px',
                                        fontWeight: 'bold',
                                        color: '#3e98c7',
                                    }}
                                >
                                    {t('surveyProgress.missedTimeSlots')}
                                </Typography>
                                <ul>
                                    {missingNotifications.map((notif, index) => (
                                        <li key={index}>
                                            {format(new Date(notif.notificationTime), 'yyyy-MM-dd HH:mm')}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <Typography
                                sx={{
                                    textAlign: 'center',
                                    color: '#d32f2f',
                                }}
                            >
                                {t('surveyProgress.noMissingNotifications')}
                            </Typography>
                        )}

                        {newTimeSlots.length > 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        marginTop: '20px',
                                        marginBottom: '12px',
                                        fontWeight: 'bold',
                                        color: '#3e98c7',
                                    }}
                                >
                                    {t('surveyProgress.newTimeSlots')}
                                </Typography>
                                <ul>
                                    {newTimeSlots
                                        .filter((slot) => !slot.hasPerformanceScore && !slot.isExtended)
                                        .map((slot, index) => (
                                            <li key={index}>
                                                {format(slot, 'yyyy-MM-dd HH:mm')}
                                            </li>
                                        ))}
                                </ul>
                            </>
                        )}
                        {missingNotifications.filter((notif) => notif.hasPerformanceScore === false && notif.isExtended === true).length > 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        marginTop: '20px',
                                        marginBottom: '12px',
                                        fontWeight: 'bold',
                                        color: '#3e98c7',
                                    }}
                                >
                                    {t('surveyProgress.assignedNotifications')}
                                </Typography>
                                <ul>
                                    {missingNotifications
                                        .filter((notif) => notif.hasPerformanceScore === false && notif.isExtended === true)
                                        .map((notif, index) => (
                                            <li key={index}>
                                                {format(new Date(notif.notificationTime), 'yyyy-MM-dd HH:mm')}
                                            </li>
                                        ))}
                                </ul>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions
                        sx={{
                            justifyContent: 'center',
                            padding: '16px 24px',
                            borderTop: '1px solid #ccc',
                        }}
                    >
                        <Button
                            onClick={() => setExtendDialogOpen(false)}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                color: '#333',
                                textTransform: 'none',
                                marginRight: '12px',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        >
                            {t('surveyProgress.cancel')}
                        </Button>
                        <Button
                            onClick={handleExtendObservation}
                            color="primary"
                            variant="contained"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                                padding: '8px 16px',
                            }}
                        >
                            {t('surveyProgress.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={errorSnackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleCloseErrorSnackbar}
                >
                    <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
                        {t('surveyProgress.restartError')}
                    </Alert>
                </Snackbar>
            </>
        )}
    </Box>
);
}
export default SurveyProgress;